import { useCollection } from '@cloudscape-design/collection-hooks';
import { ContentLayout, SelectProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { IconCaretDown, IconCaretUp } from '@tabler/icons-react';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Layout from '../../../../components/Layout';
import {
  FoodMenue,
  GetFoodMenuesDocument,
  GetFoodMenuesQuery,
  useDeleteFoodMenueMutation,
  useGetAdditivesQuery,
  useGetAllergensQuery,
  useGetFoodCategoriesQuery,
  useGetFoodMenuesQuery,
  useUpdateSortnumbersMutation,
} from '../../../../generated/graphql';
import useNotifications from '../../../../hooks/useNotifications';
import MetaTags from '../../../../lib/MetaTags';
import Create from './Create';
import Delete from './Delete';
import Edit from './Edit';
import { getMatchesCountText } from './table-config';

type EmptyStateProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
};
function EmptyState({ title, subtitle, action }: EmptyStateProps) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: 's' }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

const FoodMenues = () => {
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { data } = useGetFoodMenuesQuery({});
  const [updateSortNumbers, { loading: updatingSortNumbers }] =
    useUpdateSortnumbersMutation();
  const { data: additivesData } = useGetAdditivesQuery({
    variables: {
      Filter: {
        is_active: {
          eq: true,
        },
      },
    },
  });
  const { data: allergenesData } = useGetAllergensQuery({
    variables: {
      Filter: {
        is_active: {
          eq: true,
        },
      },
    },
  });
  const { data: foodcategoriesData } = useGetFoodCategoriesQuery({
    variables: {
      Filter: {
        is_active: {
          eq: true,
        },
      },
    },
  });

  const additiveOptions = useMemo(() => {
    if (additivesData?.listAdditives?.items) {
      return additivesData.listAdditives.items.map(
        (item) =>
          ({
            label: item?.text,
            value: item?.id,
            description: 'Zusatzstoff',
          }) as SelectProps.Option,
      );
    }
    return [];
  }, [additivesData]);

  const allergenOptions = useMemo(() => {
    if (allergenesData?.listAllergens?.items) {
      return allergenesData.listAllergens.items.map(
        (item) =>
          ({
            label: item?.text,
            value: item?.id,
            description: 'Allergen',
          }) as SelectProps.Option,
      );
    }
    return [];
  }, [allergenesData]);

  const categoryOptions = useMemo(() => {
    if (foodcategoriesData?.listFoodCategories?.items) {
      return foodcategoriesData.listFoodCategories.items.map(
        (item) =>
          ({
            label: item?.name,
            value: item?.id,
            description: item?.subtitle,
          }) as SelectProps.Option,
      );
    }
    return [];
  }, [foodcategoriesData]);

  const [deleteFoodMenue] = useDeleteFoodMenueMutation();

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const viewData = useMemo(() => {
    if (!data?.listFoodMenues?.items) return [];
    return data.listFoodMenues.items.map((item) => {
      return {
        ...item,
        price:
          new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Number(item?.price)) ?? '',
        subtitle: item?.subtitle ?? '-',
        category: item?.category?.label ?? '-',
        foodtype: item?.foodtype?.label ?? '-',
        is_active: item?.is_active ? 'Ja' : 'Nein',
        updated_at: item?.updated_at
          ? DateTime.fromSeconds(item.updated_at).toLocaleString(
              DateTime.DATETIME_MED,
            )
          : '-',
      };
    });
  }, [data]);

  const [preferences] = useState({
    contentDisplay: [
      { id: 'id', visible: false },
      { id: 'sort', visible: true },
      { id: 'category', visible: true },
      { id: 'title', visible: true },
      { id: 'subtitle', visible: true },
      { id: 'price', visible: true },
      { id: 'foodtype', visible: true },
      { id: 'is_active', visible: true },
      { id: 'updated_at', visible: true },
    ],
  });

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    actions: { setSelectedItems },
  } = useCollection(
    viewData.sort((a, b) => (a?.sortnumber ?? 0) - (b?.sortnumber ?? 0)),
    {
      filtering: {
        empty: (
          <EmptyState
            title="Keine Einträge gefunden"
            action={
              <Button onClick={() => setCreateModal(true)}>Erstellen</Button>
            }
          />
        ),
        noMatch: (
          <EmptyState
            title="Keine Einträge gefunden"
            action={
              <Button onClick={() => actions.setFiltering('')}>
                Filter zurücksetzen
              </Button>
            }
          />
        ),
      },
      sorting: {},
      selection: {},
    },
  );
  const { selectedItems, onSelectionChange } = collectionProps;

  const onDeleteDiscard = () => setDeleteModal(false);
  const onDeleteConfirm = useCallback(async () => {
    setSelectedItems([]);
    if (selectedItems && selectedItems.length > 0 && selectedItems[0]) {
      const selectedItem = selectedItems[0];
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird gelöscht...' });
      setDeleteModal(false);
      deleteFoodMenue({
        variables: { input: { id: selectedItem.id ?? '' } },
        update: (cache) => {
          const existingData = cache.readQuery({
            query: GetFoodMenuesDocument,
          }) as GetFoodMenuesQuery;
          const oldData = existingData.listFoodMenues?.items as FoodMenue[];
          const newData = oldData.filter((item) => item.id !== selectedItem.id);
          cache.writeQuery({
            query: GetFoodMenuesDocument,
            data: {
              listFoodMenues: { items: newData },
            },
          });
        },
      })
        .then(async (data) => {
          const result = data.data?.deleteFoodMenue;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${selectedItem.title}" wurde gelöscht.`,
              header: 'Gelöscht',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  }, [
    deleteFoodMenue,
    notifyFailed,
    notifyInProgress,
    selectedItems,
    setSelectedItems,
    notifyDeletedOrUpdated,
  ]);

  const handleSortDown = useCallback(
    async (item: any) => {
      // Find the index of the current item
      const currentItem = items.find((i) => i.id === item.id);
      const currentIndex = currentItem?.sortnumber;
      // Find the index of the item before (above) the current item
      let afterItem: typeof currentItem | undefined = undefined;
      const afterIndex =
        typeof currentIndex === 'number' ? currentIndex + 1 : -1;
      if (afterIndex) {
        afterItem = items.find((i) => i.sortnumber === afterIndex);
      }

      if (
        currentItem &&
        typeof currentItem.sortnumber === 'number' &&
        afterItem &&
        typeof afterItem.sortnumber === 'number'
      ) {
        updateSortNumbers({
          variables: {
            id1: currentItem.id ?? '',
            sortnumber1: currentItem.sortnumber + 1,
            id2: afterItem.id ?? '',
            sortnumber2: afterItem.sortnumber - 1,
          },
          update: (cache) => {
            // Read the current list from the cache
            const existingData = cache.readQuery({
              query: GetFoodMenuesDocument,
            }) as GetFoodMenuesQuery | null;

            if (!existingData?.listFoodMenues?.items) return;

            // Update the sortnumbers in the cached items
            const updatedItems = existingData.listFoodMenues.items.map((i) => {
              if (
                i?.id === currentItem.id &&
                typeof currentItem.sortnumber === 'number'
              ) {
                return { ...i, sortnumber: currentItem.sortnumber + 1 };
              }
              if (
                afterItem &&
                i?.id === afterItem.id &&
                typeof afterItem.sortnumber === 'number'
              ) {
                return { ...i, sortnumber: afterItem.sortnumber - 1 };
              }
              return i;
            });

            // Write the updated list back to the cache
            cache.writeQuery({
              query: GetFoodMenuesDocument,
              data: {
                listFoodMenues: {
                  ...existingData.listFoodMenues,
                  items: updatedItems,
                },
              },
            });
          },
        });
      }
    },
    [items, updateSortNumbers],
  );

  const handleSortUp = useCallback(
    async (item: any) => {
      // Find the index of the current item
      const currentItem = items.find((i) => i.id === item.id);
      const currentIndex = currentItem?.sortnumber;
      // Find the index of the item before (above) the current item
      let beforeItem: typeof currentItem | undefined = undefined;
      const beforeIndex =
        currentIndex && currentIndex > 0 ? currentIndex - 1 : -1;
      if (beforeIndex >= 0) {
        beforeItem = items.find((i) => i.sortnumber === beforeIndex);
      }

      if (
        currentItem &&
        typeof currentItem.sortnumber === 'number' &&
        beforeItem &&
        typeof beforeItem.sortnumber === 'number'
      ) {
        updateSortNumbers({
          variables: {
            id1: currentItem.id ?? '',
            sortnumber1: currentItem.sortnumber - 1,
            id2: beforeItem.id ?? '',
            sortnumber2: beforeItem.sortnumber + 1,
          },
          update: (cache) => {
            // Read the current list from the cache
            const existingData = cache.readQuery({
              query: GetFoodMenuesDocument,
            }) as GetFoodMenuesQuery | null;

            if (!existingData?.listFoodMenues?.items) return;

            // Update the sortnumbers in the cached items
            const updatedItems = existingData.listFoodMenues.items.map((i) => {
              if (
                i?.id === currentItem.id &&
                typeof currentItem.sortnumber === 'number'
              ) {
                return { ...i, sortnumber: currentItem.sortnumber - 1 };
              }
              if (
                beforeItem &&
                i?.id === beforeItem.id &&
                typeof beforeItem.sortnumber === 'number'
              ) {
                return { ...i, sortnumber: beforeItem.sortnumber + 1 };
              }
              return i;
            });

            // Write the updated list back to the cache
            cache.writeQuery({
              query: GetFoodMenuesDocument,
              data: {
                listFoodMenues: {
                  ...existingData.listFoodMenues,
                  items: updatedItems,
                },
              },
            });
          },
        });
      }
    },
    [items, updateSortNumbers],
  );

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[
        { text: 'Webseite', href: '/website' },
        { text: 'Speisekarte', href: '/website/food' },
        {
          text: 'Speisen',
          href: '/website/food/menues',
        },
      ]}
    >
      <MetaTags title="Speisen | Theo's Bistro" />
      <ContentLayout header={<Header variant="h1">Speisen</Header>}>
        <SpaceBetween size="m">
          <Table
            {...collectionProps}
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
              `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            stickyColumns={{ first: 1, last: 0 }}
            columnDefinitions={[
              {
                id: 'sort',
                header: 'Sort',
                cell: (e) => {
                  if (typeof e.sortnumber === 'number') {
                    return (
                      <div className="flex flex-1 flex-row justify-center items-center">
                        {e.sortnumber >= 0 &&
                        e.sortnumber < items.length - 1 ? (
                          <IconCaretDown
                            className={updatingSortNumbers ? 'opacity-25' : ''}
                            onClick={
                              updatingSortNumbers
                                ? () => {}
                                : () => handleSortDown(e)
                            }
                            style={{ cursor: 'pointer' }}
                          />
                        ) : null}
                        {e.sortnumber > 0 &&
                        e.sortnumber <= items.length - 1 ? (
                          <IconCaretUp
                            className={updatingSortNumbers ? 'opacity-25' : ''}
                            onClick={
                              updatingSortNumbers
                                ? () => {}
                                : () => handleSortUp(e)
                            }
                            style={{ cursor: 'pointer' }}
                          />
                        ) : null}
                      </div>
                    );
                  }
                },
                isRowHeader: true,
              },
              {
                id: 'category',
                header: 'Kategoriename',
                cell: (e) => e.category,
                isRowHeader: true,
              },
              {
                id: 'title',
                header: 'Titel',
                cell: (e) => e.title,
                isRowHeader: true,
              },
              {
                id: 'subtitle',
                header: 'Untertitel',
                cell: (e) => e.subtitle,
                isRowHeader: true,
              },
              {
                id: 'price',
                header: 'Preis',
                cell: (e) => e.price,
                isRowHeader: true,
              },
              {
                id: 'foodtype',
                header: 'Typ',
                cell: (e) => e.foodtype,
                isRowHeader: true,
              },
              {
                id: 'is_active',
                header: 'Aktive',
                cell: (e) => e.is_active,
              },
              {
                id: 'updated_at',
                header: 'Zuletzt bearbeitet',
                cell: (e) => e.updated_at,
                isRowHeader: true,
              },
            ]}
            columnDisplay={preferences.contentDisplay}
            enableKeyboardNavigation
            items={items}
            loadingText="Lade ..."
            selectionType="single"
            trackBy="id"
            filter={
              <TextFilter
                {...filterProps}
                countText={getMatchesCountText(filteredItemsCount)}
                filteringPlaceholder="Einträge durchsuchen"
              />
            }
            header={
              <Header
                counter={
                  selectedItems && selectedItems.length
                    ? `(${selectedItems.length}/${viewData.length})`
                    : `(${viewData.length})`
                }
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <ButtonDropdown
                      onItemClick={(event) => {
                        event.preventDefault();
                        setDeleteModal(true);
                      }}
                      disabled={isEmpty(selectedItems)}
                      items={[
                        {
                          id: 'delete',
                          text: 'Löschen',
                          external: false,
                        },
                      ]}
                      mainAction={{
                        disabled: isEmpty(selectedItems),
                        onClick: () => setEditModal(true),
                        text: 'Bearbeiten',
                      }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => setCreateModal(true)}
                    >
                      Erstellen
                    </Button>
                  </SpaceBetween>
                }
              >
                Einträge
              </Header>
            }
          />
        </SpaceBetween>
        <Create
          setVisible={setCreateModal}
          visible={createModal}
          notifyInProgress={notifyInProgress}
          notifyDeletedOrUpdated={notifyDeletedOrUpdated}
          notifyFailed={notifyFailed}
          additiveOptions={additiveOptions}
          allergenOptions={allergenOptions}
          categoryOptions={categoryOptions}
        />
        {selectedItems && selectedItems.length > 0 ? (
          <>
            <Edit
              setVisible={setEditModal}
              visible={editModal}
              selectedId={selectedItems[0].id}
              notifyInProgress={notifyInProgress}
              notifyDeletedOrUpdated={notifyDeletedOrUpdated}
              notifyFailed={notifyFailed}
              additiveOptions={additiveOptions}
              allergenOptions={allergenOptions}
              categoryOptions={categoryOptions}
            />
            <Delete
              visible={deleteModal}
              onDiscard={onDeleteDiscard}
              onDelete={onDeleteConfirm}
              selectedItems={selectedItems}
            />
          </>
        ) : null}
      </ContentLayout>
    </Layout>
  );
};

export default FoodMenues;
